// Turns raw punch events into a per-day week summary (Present/Absent/
// Weekend/Holiday + hours worked) for the week-table and week-timeline
// widgets. Pure functions, no fetching — screens own the queries.
//
// Punches are paired into sessions by walking the FULL sorted punch list
// (not bucketed by calendar day first), because a session can span
// midnight — an overnight clock_out lands on the next day's punchedAt but
// still belongs to the day its clock_in happened on.

export interface AttendancePunch {
  punchType: 'clock_in' | 'clock_out';
  punchedAt: string;
}

export type DayStatus = 'weekend' | 'holiday' | 'present' | 'absent' | 'today' | 'none' | 'future';

export interface DaySummary {
  dateKey: string; // YYYY-MM-DD
  dayShort: string; // 'Sun'..'Sat'
  dayNum: number;
  isToday: boolean;
  isFuture: boolean;
  status: DayStatus;
  statusLabel: string;
  rangeLabel: string; // e.g. "12:31 PM - 10:53 AM" or "No check-in - No check-out"
  hoursLabel: string; // e.g. "22:22 Hrs", '' when not applicable
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function shiftKey(dateKey: string, deltaDays: number): string {
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

// The 7 date keys (Sun..Sat) for the week containing `anchorKey`.
export function getWeekKeys(anchorKey: string): string[] {
  const anchor = new Date(`${anchorKey}T00:00:00.000Z`);
  const sunday = new Date(anchor);
  sunday.setUTCDate(anchor.getUTCDate() - anchor.getUTCDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setUTCDate(sunday.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

// Shift.startTime/endTime are @db.Time values serialized as a
// 1970-01-01-anchored ISO datetime — read with UTC fields or the displayed
// hour drifts by the viewer's timezone offset.
export function fmtShiftTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}

function formatDuration(ms: number): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

interface Session {
  inAt: string;
  outAt: string | null;
}

function buildSessions(entries: AttendancePunch[]): Session[] {
  const sorted = [...entries].sort((a, b) => a.punchedAt.localeCompare(b.punchedAt));
  const sessions: Session[] = [];
  let open: string | null = null;
  for (const e of sorted) {
    if (e.punchType === 'clock_in') {
      // A second clock-in with no clock-out in between closes the prior
      // session as incomplete rather than silently dropping it.
      if (open) sessions.push({ inAt: open, outAt: null });
      open = e.punchedAt;
    } else if (open) {
      sessions.push({ inAt: open, outAt: e.punchedAt });
      open = null;
    }
  }
  if (open) sessions.push({ inAt: open, outAt: null });
  return sessions;
}

export function buildWeekSummaries(params: {
  weekKeys: string[];
  entries: AttendancePunch[];
  weeklyDayOff?: string;
  holidayNameByDate: Map<string, string>;
  nowMs: number;
}): DaySummary[] {
  const { weekKeys, entries, weeklyDayOff, holidayNameByDate, nowMs } = params;
  const today = new Date(nowMs).toISOString().slice(0, 10);

  const byDay = new Map<string, Session[]>();
  for (const s of buildSessions(entries)) {
    const key = s.inAt.slice(0, 10);
    const arr = byDay.get(key) ?? [];
    arr.push(s);
    byDay.set(key, arr);
  }

  return weekKeys.map((dateKey) => {
    const d = new Date(`${dateKey}T00:00:00.000Z`);
    const dayShort = DAY_NAMES[d.getUTCDay()];
    const dayNum = d.getUTCDate();
    const isFuture = dateKey > today;
    const isToday = dateKey === today;
    const holidayName = holidayNameByDate.get(dateKey);
    const isWeekend = !!weeklyDayOff && dayShort === weeklyDayOff;
    const isHoliday = !isWeekend && !!holidayName;
    const daySessions = byDay.get(dateKey) ?? [];

    let status: DayStatus = 'none';
    let statusLabel = '';
    let rangeLabel = '';
    let hoursLabel = '';

    if (isWeekend) {
      status = 'weekend';
      statusLabel = 'Weekend';
      rangeLabel = 'No check-in - No check-out';
    } else if (isHoliday) {
      status = 'holiday';
      statusLabel = holidayName as string;
      rangeLabel = 'No check-in - No check-out';
    } else if (daySessions.length === 0) {
      if (isFuture) {
        status = 'future';
      } else if (isToday) {
        status = 'none';
      } else {
        status = 'absent';
        statusLabel = 'Absent';
        rangeLabel = 'No check-in - No check-out';
      }
    } else {
      const earliestIn = daySessions.reduce((min, s) => (s.inAt < min ? s.inAt : min), daySessions[0].inAt);
      const openSession = daySessions.find((s) => !s.outAt);
      const closedMs = daySessions
        .filter((s): s is Session & { outAt: string } => !!s.outAt)
        .reduce((sum, s) => sum + (new Date(s.outAt).getTime() - new Date(s.inAt).getTime()), 0);

      if (openSession) {
        rangeLabel = `${fmtTime(earliestIn)} - No check-out`;
        if (isToday) {
          status = 'today';
          const elapsedMs = closedMs + (nowMs - new Date(openSession.inAt).getTime());
          hoursLabel = `${formatDuration(elapsedMs)} Hrs`;
        } else {
          // Past day, punched in but never out — no way to know actual
          // hours worked, so it's treated as Absent rather than guessed.
          status = 'absent';
          statusLabel = 'Absent';
        }
      } else {
        const lastOut = daySessions.reduce(
          (max, s) => (s.outAt && s.outAt > max ? s.outAt : max),
          daySessions[0].outAt as string,
        );
        rangeLabel = `${fmtTime(earliestIn)} - ${fmtTime(lastOut)}`;
        status = 'present';
        statusLabel = 'Present';
        hoursLabel = `${formatDuration(closedMs)} Hrs`;
      }
    }

    return { dateKey, dayShort, dayNum, isToday, isFuture, status, statusLabel, rangeLabel, hoursLabel };
  });
}
